import { useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import SortableFieldGrid from "../SortableFieldGrid";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";
import PrefrenceSelectors from "@/components/ui/others/PrefrenceRenderers/PrefrenceSelectors";
import PrefrenceTextField from "@/components/ui/others/PrefrenceRenderers/PrefrenceTextField";
import PrefrenceFormattedField from "@/components/ui/others/PrefrenceRenderers/PrefrenceFormattedField";
import { isFormattedCfType } from "@/features/customField/utils/cfValueTypes";
import { getChildModuleScopes } from "./getChildModuleScopes";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import useResolveCustomDefaults, {
  type ResolvedField,
} from "@/features/customField/api/useResolveCustomDefaults";
import { lead_spesific_key, child_spesific_key } from "@/config/endpoints";
import { usePermissions } from "@/rbac/usePermissions";
import {
  DEAL_EDIT_DENIED_MESSAGE,
  dealStageIdOf,
  useCanEditDeal,
} from "@/rbac/useCanEditDeal";
import {
  canAccessCustomField,
  filterCustomInputFieldsByViewPermission,
} from "@/rbac/customFieldValueAccess";

const LEAD_PREF_HINT =
  "Fields that apply to the whole lead — shared across every deal under this lead.";
const CHILD_PREF_HINT =
  "Fields specific to this deal only — they don't apply to other deals on this lead.";

function SectionHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <CardTitle className="text-[14px] font-medium text-[var(--color-black-6)] tracking-[-0.28px] leading-normal">
        {title}
      </CardTitle>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${title}`}
            className="text-[var(--color-black-5)] hover:text-[var(--color-black-7)] transition-colors"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-xs leading-snug">
          {hint}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function buildAlignedScopes(fields: any[], currentScopes: any[]) {
  const safeScopes = (currentScopes ?? []).filter(
    (s): s is Record<string, unknown> => s != null
  );
  return fields.map((field: any) => {
    const existing = safeScopes.find(
      (s: any) => Number(s?.customInputId) === Number(field.id)
    );
    if (existing) {
      // Defensive: SelectItem values are `String(op.id)`, so optionIds must
      // also be strings or the dropdown silently falls back to its placeholder
      // when the saved value is numeric.
      const rawOptionIds = (existing as any).optionIds;
      const optionIds = Array.isArray(rawOptionIds)
        ? rawOptionIds.map((id: unknown) => String(id))
        : [];
      return { ...(existing as any), optionIds };
    }
    if (
      field.type === "TEXT_FIELD" ||
      field.type === "REMARK" ||
      isFormattedCfType(field.type)
    ) {
      return { customInputId: field.id, optionIds: [], textValue: "" };
    }
    return { customInputId: field.id, optionIds: [] };
  });
}

/**
 * True when `currentScopes` is already aligned 1:1 with `fields` by
 * `customInputId` (and `optionIds` are strings). Used to short-circuit
 * the alignment effect so we don't loop on our own setValue.
 */
function isScopesAligned(fields: any[], currentScopes: any[]) {
  if (currentScopes.length !== fields.length) return false;
  for (let i = 0; i < fields.length; i++) {
    const scope = currentScopes[i];
    if (scope == null) return false;
    if (Number(scope.customInputId) !== Number(fields[i].id)) return false;
    const optionIds = scope.optionIds;
    if (Array.isArray(optionIds)) {
      for (const id of optionIds) {
        if (typeof id !== "string") return false;
      }
    }
  }
  return true;
}

function renderField(
  val: any,
  index: number,
  control: any,
  errors: any,
  readOnly: boolean,
  resolvedOptions?: { id: number; value: string }[],
) {
  switch (val.type) {
    case "SELECTOR":
      return (
        <PrefrenceSelectors
          key={val.id}
          val={val}
          index={index}
          errors={errors}
          control={control}
          readOnly={readOnly}
          resolvedOptions={resolvedOptions}
        />
      );
    case "TEXT_FIELD":
      return (
        <PrefrenceTextField
          key={val.id}
          val={val}
          index={index}
          errors={errors}
          control={control}
          readOnly={readOnly}
        />
      );
    case "REMARK":
      return (
        <PrefrenceTextField
          key={val.id}
          val={val}
          index={index}
          errors={errors}
          control={control}
          isRemark
          readOnly={readOnly}
        />
      );
    case "NUMBERS":
    case "PERCENTAGE":
    case "DATE":
      return (
        <PrefrenceFormattedField
          key={val.id}
          val={val}
          index={index}
          errors={errors}
          control={control}
          readOnly={readOnly}
        />
      );
    default:
      return null;
  }
}

export default function PreferenceEditMode() {
  const { control, errors, watch: leadWatch, setValue } = useLeadForm();
  const { isChildActive, lead, child } = useActiveDataMutation();
  const childContext = useChildForm();

  const { spesificFields } = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
    stageId: lead?.stage?.id,
    campaignId: lead?.campaign?.id,
  });

  const { spesificFields: spesificsChild } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    stageId: child?.stage?.id,
    isActive: isChildActive,
  });

  const { canGlobal, getRawGlobalModule } = usePermissions();
  // Pass the raw reader too: without it the per-field gate cannot tell a
  // field the role simply never configured from one it explicitly denied.
  const cfPerms = { canGlobal, getRawGlobalModule };
  const visibleLeadFields = filterCustomInputFieldsByViewPermission(
    spesificFields,
    cfPerms
  );
  // Tenant-wide field order per section (Super Admins drag the grips).
  const leadPrefOrder = useLeadViewFieldOrder("leadPref");
  const dealPrefOrder = useLeadViewFieldOrder("dealPref");
  const visibleChildFields = isChildActive
    ? filterCustomInputFieldsByViewPermission(spesificsChild, cfPerms)
    : [];

  /**
   * Editability for the CFs on this drawer follows Lead Edit, not the
   * per-field `custom_field_<id>.edit` toggle. The per-field rule still
   * decides VISIBILITY (`view`) — fields the user can't view are filtered
   * out above — but once a CF is visible, the user's broader Lead Edit
   * grant is what controls write. Otherwise a role with Lead Edit + a CF
   * left on "View only" looked editable in spirit but greyed in practice,
   * and the user gets a "no permission" error on save anyway because the
   * write path is gated by Lead Edit upstream.
   */
  const canEditLead = canGlobal("lead_management", "edit");
  // The deal's own CFs additionally need "Edit choices" on the DEAL's stage.
  const canEditDeal = useCanEditDeal();
  const canEditActiveDeal = canEditLead && canEditDeal(dealStageIdOf(child));

  // Mirror the Add-Application flow: the dropdown for each child-level CF
  // shows only options that the deal's attached niches (course, score,
  // etc.) actually support. Server walks FK relations from these primary
  // niche items, so we only push top-level entries from `child.modules`.
  // No moduleScopes → resolver returns nothing → selector falls back to
  // showing all options (matches legacy behaviour for deals with nothing
  // attached).
  const childModuleScopes = useMemo(
    () => (isChildActive ? getChildModuleScopes(child) : []),
    [isChildActive, child],
  );
  const { resolvedFields: childResolvedFields } = useResolveCustomDefaults(
    childModuleScopes,
    "child",
  );
  const childResolvedMap = useMemo(
    () =>
      new Map<number, ResolvedField>(
        childResolvedFields.map((rf) => [rf.customInputId, rf]),
      ),
    [childResolvedFields],
  );

  // Subscribe to the form's `customInputScopes` so the alignment effect
  // re-runs after `ChildProvider`/`FormLeadContext` resets the form on
  // child-data load (previously it raced once on mount, then stayed stale).
  // The `<Controller name="customInputScopes.${index}">` binding in the
  // selector/text renderers depends on the array being aligned 1:1 with
  // `spesificFields` — when the API returns fewer scopes than the section
  // has fields, fields beyond the API length get `undefined` and the
  // dropdown shows its placeholder even though view mode shows the value.
  const leadScopesWatched = leadWatch("customInputScopes");
  const childScopesWatched = childContext.watch("customInputScopes");

  useEffect(() => {
    if (!spesificFields?.length) return;
    const currentScopes = leadScopesWatched ?? [];
    if (isScopesAligned(spesificFields, currentScopes)) return;
    const aligned = buildAlignedScopes(spesificFields, currentScopes);
    setValue("customInputScopes", aligned, { shouldDirty: false });
  }, [spesificFields, leadScopesWatched, setValue]);

  useEffect(() => {
    if (!isChildActive || !spesificsChild?.length) return;
    const currentScopes = childScopesWatched ?? [];
    if (isScopesAligned(spesificsChild, currentScopes)) return;
    const aligned = buildAlignedScopes(spesificsChild, currentScopes);
    childContext.setValue("customInputScopes", aligned, { shouldDirty: false });
  }, [spesificsChild, isChildActive, childScopesWatched, childContext]);

  if (!visibleLeadFields.length && !visibleChildFields.length) {
    return null;
  }

  // Keep the ORIGINAL index of every field: `<Controller
  // name={"customInputScopes.${i}"}>` binds by position against the aligned
  // scopes array built from `spesificFields` above, so the display order
  // (tenant-wide, draggable) must never change which value a field edits.
  // Fields the user can't view are skipped, not shifted.
  const leadItems = leadPrefOrder
    .sortByOrder(
      (spesificFields ?? [])
        .map((val: any, i: number) => ({ val, i }))
        .filter(({ val }: { val: any; i: number }) =>
          canAccessCustomField(val, "view", cfPerms),
        ),
      ({ val }: { val: any; i: number }) => String(val.id),
    )
    .map(({ val, i }: { val: any; i: number }) => ({
      key: String(val.id),
      // Single vs multi comes from the field itself (Tag / Normal), so a
      // field renders the same way here and on the deal below.
      node: renderField(val, i, control, errors, !canEditLead),
    }));

  const childItems = dealPrefOrder
    .sortByOrder(
      (spesificsChild ?? [])
        .map((val: any, i: number) => ({ val, i }))
        .filter(({ val }: { val: any; i: number }) =>
          canAccessCustomField(val, "view", cfPerms),
        ),
      ({ val }: { val: any; i: number }) => String(val.id),
    )
    .map(({ val, i }: { val: any; i: number }) => ({
      key: String(val.id),
      node: renderField(
        val,
        i,
        childContext.control,
        childContext.errors,
        !canEditActiveDeal,
        childResolvedMap.get(val.id)?.resolvedOptions,
      ),
    }));

  return (
    <Card className="border-var(--color-black-1) rounded-[16px] gap-[16px] p-[16px] shadow-none">
      <CardHeader className="px-0 pb-0">
        <CardTitle className="text-[16px] font-medium text-black tracking-[-0.32px] leading-normal">
          Preference
        </CardTitle>
      </CardHeader>

      {visibleLeadFields.length > 0 && (
        <CardHeader className="px-0 pb-0 pt-0">
          <SectionHeading title="Lead Preference" hint={LEAD_PREF_HINT} />
        </CardHeader>
      )}

      {visibleLeadFields.length > 0 && (
        <CardContent className="px-2 pb-4">
          <SortableFieldGrid
            columns={2}
            items={leadItems}
            canReorder={leadPrefOrder.canReorder}
            onReorder={leadPrefOrder.save}
          />
        </CardContent>
      )}

      {isChildActive &&
        visibleChildFields.length > 0 &&
        visibleLeadFields.length > 0 && (
          <div className="h-px bg-[#e6e8e7]" />
        )}

      {isChildActive && visibleChildFields.length > 0 && (
        <>
          <CardHeader className="px-0 pb-0 pt-0">
            <SectionHeading
              title="Child/Deal Preference"
              hint={
                canEditLead && !canEditActiveDeal
                  ? `${CHILD_PREF_HINT} ${DEAL_EDIT_DENIED_MESSAGE}.`
                  : CHILD_PREF_HINT
              }
            />
          </CardHeader>
          <CardContent className="px-2 pb-4">
            <SortableFieldGrid
              columns={2}
              items={childItems}
              canReorder={dealPrefOrder.canReorder}
              onReorder={dealPrefOrder.save}
            />
          </CardContent>
        </>
      )}
    </Card>
  );
}
